import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { useEffect } from 'react';
import { useThemeStore } from './stores/theme.store.js';

// Layout
import { MainLayout } from './components/layout/MainLayout.js';

// Public pages
import HomePage from './pages/public/HomePage.js';
import SearchPage from './pages/public/SearchPage.js';
import SongPage from './pages/public/SongPage.js';
import ArtistPage from './pages/public/ArtistPage.js';
import AlbumPage from './pages/public/AlbumPage.js';
import GenrePage from './pages/public/GenrePage.js';
import ChartsPage from './pages/public/ChartsPage.js';
import NotFoundPage from './pages/public/NotFoundPage.js';

// Auth pages
import { LoginPage } from './pages/public/LoginPage.js';
import { RegisterPage } from './pages/public/RegisterPage.js';
import { ForgotPasswordPage } from './pages/public/ForgotPasswordPage.js';

// Legal pages
import { TermsPage } from './pages/legal/TermsPage.js';
import { PrivacyPage } from './pages/legal/PrivacyPage.js';
import { DmcaPage } from './pages/legal/DmcaPage.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function ThemeInitializer() {
  const { theme } = useThemeStore();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return null;
}

export function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ThemeInitializer />
          <Routes>
            <Route element={<MainLayout />}>
              {/* Public */}
              <Route path="/" element={<HomePage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/songs/:slug" element={<SongPage />} />
              <Route path="/artists/:slug" element={<ArtistPage />} />
              <Route path="/albums/:slug" element={<AlbumPage />} />
              <Route path="/genres" element={<GenrePage />} />
              <Route path="/genres/:slug" element={<GenrePage />} />
              <Route path="/charts/:period" element={<ChartsPage />} />
              <Route path="/trending" element={<ChartsPage />} />

              {/* Auth */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />

              {/* Legal */}
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/dmca" element={<DmcaPage />} />

              {/* 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: 'var(--color-surface)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-border)',
              },
            }}
          />
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  );
}
